import * as React from "react"
import { ChangeEventHandler, ComponentType, FocusEventHandler, forwardRef, ReactNode, useCallback, useEffect, useMemo, useRef, useState } from "react"
import {
    FilledInputProps,
    FilledTextFieldProps,
    Stack,
    StandardTextFieldProps,
    styled,
    TextField as MuiTextField,
    TextFieldProps as MuiTextFieldProps,
} from "@mui/material"
import { useLocalization } from "@tm/localization"
import { IconButton } from "../button"
import { Icon } from "../../DataDisplay/Icons"
import { Typography } from "../../DataDisplay/typographie"
import { getTextFieldStyles, StyledTooltip } from "./styles"

export type CustomTextFieldProps = (MuiTextFieldProps | StandardTextFieldProps | FilledTextFieldProps) & {
    variant?: "filled" | "standard" | "outlined" | undefined
    inputCounter?: boolean
    clearButton?: boolean
    errorHandling?: ErrorHandling[] | false
    validateNow?: boolean
    onValidationChange?(hasValidationError: boolean): void
}

export type ErrorHandling = {
    pattern?: string
    errorMessage: string
}

const StyledTextField = styled(MuiTextField, {
    shouldForwardProp: (prop) =>
        !["onValidationChange", "errorHandling", "inputCounter", "clearButton", "endAdornment", "disableUnderline"].includes(prop as string),
})(getTextFieldStyles) as ComponentType<CustomTextFieldProps>
/**
 * The `TextField` is a NOT the real Material TextBox.
 * It overrides a lot of styles to looks like our well known TextField
 *
 * return <TextField id="time" type="time" inputProps={inputProps} />;
 * ```
 *
 * API:
 * - [TextField API](https://mui.com/api/text-field/)
 * - inherits [FormControl API](https://mui.com/api/form-control/)
 */
export const TextField = forwardRef<HTMLDivElement, CustomTextFieldProps>((props, ref) => {
    const { translateText } = useLocalization()
    const inputRefLocal = useRef<HTMLInputElement>(null)
    const inputRef = (typeof props.inputRef !== "function" ? props.inputRef : undefined) ?? inputRefLocal
    const { onBlur, onChange, error, helperText, required, label, ...restProps } = props
    const [errorMessage, setErrorMessage] = useState<ReactNode | undefined>()
    const [validateNow, setValidateNow] = useState(false)

    useEffect(() => {
        if (props.type === "number") {
            console.warn("Please do not use TextField with type number, use AmountField or CurrencyField instead!")
        }
    }, [])

    useEffect(() => {
        setValidateNow(!!props.validateNow)
    }, [props.validateNow])

    useEffect(() => setErrorMessage(helperText), [helperText])

    useEffect(() => {
        setValidateNow(true)
    }, [props.errorHandling, props.value, props.required])

    const errorHandling = useMemo<ErrorHandling[] | undefined>(() => {
        if (props.errorHandling === false) {
            return
        }
        if (props.required && !props.errorHandling?.length) {
            return [{ pattern: "\\w+", errorMessage: translateText(13831) }]
        }
        return props.errorHandling ?? []
    }, [props.errorHandling, props.required])

    const setRawInputValue = useCallback((val: string) => {
        if (inputRef.current) {
            // https://stackoverflow.com/questions/23892547/what-is-the-best-way-to-trigger-onchange-event-in-react-js/46012210#46012210
            // React doesn't recognize the setting of  ref.value = ""
            const inputPrototype = props.multiline ? window.HTMLTextAreaElement.prototype : window.HTMLInputElement.prototype

            const currentInputRef = inputRef.current.node ?? inputRef.current

            const nativeInputValueSetter = Object.getOwnPropertyDescriptor(inputPrototype, "value")?.set
            nativeInputValueSetter?.call(currentInputRef, val)
            currentInputRef.dispatchEvent(new Event("input", { bubbles: true, cancelable: true }))
            currentInputRef.focus({})
        }
    }, [])

    const resetValue = () => {
        setRawInputValue("")
    }

    function validate() {
        setValidateNow(false)
        if (errorHandling?.length) {
            const value = String(props.value ?? "")
            const errorWithoutPattern = errorHandling.find((handling) => !handling.pattern && !!handling.errorMessage)

            if (errorWithoutPattern) {
                setErrorMessage(errorWithoutPattern.errorMessage)
                return false
            }

            const errorWithPattern = errorHandling.find((val) => !!val.pattern && !value.match(val.pattern))

            if (errorWithPattern) {
                setErrorMessage(errorWithPattern.errorMessage)
                return false
            }
        }

        setErrorMessage(undefined)
        return true
    }

    useEffect(() => {
        if (validateNow) {
            validate()
        }
    }, [validateNow])

    const hasValidationError = error || !!errorMessage
    const requireEndAdornment = props.InputProps?.endAdornment || props.inputCounter || props.clearButton || hasValidationError

    useEffect(() => {
        props.onValidationChange?.(hasValidationError)
    }, [hasValidationError, props.onValidationChange])

    const overwrittenInputProps: Partial<FilledInputProps> = {
        disableUnderline: true,
        ...props.InputProps,
        sx: { pr: 0.5, alignItems: "end" },
        endAdornment: requireEndAdornment ? (
            <Stack direction="row" gap={0.5} alignItems="center" pb="2px" height="30px">
                {props.inputCounter && (
                    <Typography className="inputCounter" variant="label">
                        {(props.value as string)?.length ?? 0}
                        {!!props.inputProps?.maxLength && `/${props.inputProps.maxLength}`}
                    </Typography>
                )}
                {(props.InputProps?.endAdornment || props.clearButton) && (
                    <Stack direction="row" gap={0.5} alignItems="center" className="inputButtons">
                        {props.InputProps?.endAdornment}
                        {props.clearButton && (
                            <IconButton
                                size="small"
                                sx={{ padding: props.inputCounter ? "0 2px 2px 0" : undefined, visibility: props.value ? "visible" : "hidden" }}
                                disableTouchRipple
                                onClick={resetValue}
                            >
                                <Icon name="close" />
                            </IconButton>
                        )}
                    </Stack>
                )}
                {hasValidationError && (
                    <StyledTooltip title={errorMessage}>
                        <Icon name="alert_W_warning" color="error" sx={{ cursor: "default" }} />
                    </StyledTooltip>
                )}
            </Stack>
        ) : undefined,
    }

    const onChangeHandler: ChangeEventHandler<HTMLInputElement> = useCallback(
        (e) => {
            const currentTargetValue = e.currentTarget?.value ?? e.target.value

            setRawInputValue(currentTargetValue)
            onChange?.({
                ...e,
                target: {
                    ...e.target,
                    value: currentTargetValue,
                },
            })
        },
        [onChange, setRawInputValue]
    )

    const onBlurHandler: FocusEventHandler<HTMLTextAreaElement | HTMLInputElement> = useCallback(
        (e) => {
            setValidateNow(true)
            if (!(e.relatedTarget && e.currentTarget?.parentElement?.contains(e.relatedTarget)) && onBlur) {
                onBlur(e)
            }
        },
        [onBlur]
    )

    return (
        <StyledTextField
            ref={ref}
            inputRef={inputRef}
            variant={props.variant || "filled"}
            autoComplete="off"
            size={props.size || "medium"}
            {...restProps}
            label={
                label ? (
                    <>
                        {label}
                        {required && <span className="MuiInputLabel-asterisk">*</span>}
                    </>
                ) : undefined
            }
            onBlur={onBlurHandler}
            onChange={onChangeHandler}
            InputProps={overwrittenInputProps}
            value={props.value ?? ""}
            error={hasValidationError}
        />
    )
})
